import { SortDirection } from '@angular/material/sort';
import { IBcxGeolocationModel } from '../generic/generic-interfaces';
import { IUserModel } from '../users/user-interfaces';
import { IVehicleAssignmentModel } from '../vehicles/vehicle-interfaces';

/**
 * Order interfaces for strong typing
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2024 Build Centrix
 */

/*************************************************** enums ***********************************************************/

/**
 * Allowed Order Lookup Types
 * Note: Currently, only 'vehicleAssignments' is supported which includes
 * applicable assignments data from the mongo `orders.shipping.vehicles.assignments`
 */
export enum AllowedLookupTypes {
  VEHICLE_ASSIGNMENTS = 'vehicleAssignments',
}

/**
 * Order Types
 * Current available order types are work and lookahead orders
 */
export enum OrderTypes {
  WORK = 'work',
  LOOKAHEAD = 'lookahead',
}

/**
 * Order UI Sections
 * Primarily used on the manage order view
 */
export enum OrderUiSections {
  ATTACHMENTS = 'attachments',
  DATES = 'dates',
  DETAILS = 'details',
  FORMFIELDS = 'formFields',
  INSTRUCTIONS = 'instructions',
  JOB = 'job',
  MESSAGES = 'messages',
  SHIPPING = 'delivery',
  STATUSES = 'statuses',
}

export enum OrderShippingOptions {
  ADDRESS = 'address',
  JOBSITE = 'jobSite',
}

/************************************************ interfaces **********************************************************/

/**
 * Adjustments
 */
export interface IOrderAdjustmentModel {
  index?: number;
  id?: string;
  label: string;
  description: string;
  gauge: IGaugeOption;
  group: IAdjustmentFulfillmentGroupModel;
  cost: IValueModel;
  material: IMaterialOption;
  price: IValueModel;
  shopLabour: IValueModel;
  weight: IValueModel;
}

/**
 * Address model
 */
export interface IAddressModel {
  company?: string;
  street?: string;
  city?: string;
  state?: {
    label: string;
  };
  country?: {
    label: string;
  };
  zipcode?: string;
  phone?: string;
}

/**
 * Adjustment fulfillment group model
 */
export interface IAdjustmentFulfillmentGroupModel {
  id: number;
  label: string;
}

/**
 * Common translations used on the list order view
 */
export interface IListOrdersViewTranslations {
  orders?: string;
  orderType?: string;
}

/**
 * Fulfillment group model
 */
export interface IFulfillmentGroupModel {
  index: number;
  label: string;
  code: string;
  module: string;
  sortOrder: number;
}

/**
 * Fulfillment Group Option
 */
export interface IFulfillmentGroupOption {
  id: number;
  label: string;
}

/**
 * Gauge Option
 */
export interface IGaugeOption {
  value: string;
}

/**
 * Job form model
 */
export interface IJobFormModel {
  job: IOrderJobFormModel;
  phase: IOrderJobPhaseFormModel;
}

/**
 * Loading zone model
 */
export interface ILoadingZoneModel {
  id: string;
  label: string;
}

/**
 * Order job phase form model
 */
export interface IOrderJobPhaseFormModel {
  id: string;
  label: string;
  code: string;
}

/**
 * Order job form model
 */
export interface IOrderJobFormModel {
  created: string;
  description: string;
  index: number;
  label: string;
  name: string;
  number: string;
  status: string;
  override: IOrderJobFormModel;
  phase: IOrderJobPhaseDetailsFormModel;
}

/**
 * Order job phase details form model
 */
export interface IOrderJobPhaseDetailsFormModel {
  id: string;
  code: string;
  label: string;
}

/**
 * Lookaheads
 */
export interface IOrderLookaheadModel {
  index: number;
  label: string;
  description: string;
  orderedFor: any;
  job: any;
  phase: any;
  measuredBy: string;
  poNumber: string;
  userGroup: any;
  dateOrdered: Date;
  dateRequested: Date;
  dateProduction: Date;
  dateShipping: Date;
}

/**
 * Material Option
 */
export interface IMaterialOption {
  id: number;
  label: string;
}

/**
 * Order Options Model
 */
export interface IOrderOptionsModel {
  orderFulfillmentGroups?: Array<IFulfillmentGroupModel>;
  orderShippingLoadingZones?: Array<ILoadingZoneModel>;
  orderShippingTypes?: Array<IShippingTypeModel>;
  orderShippingVehicleAssignments?: Array<IVehicleAssignmentModel>;
}

/**
 * Order date type
 */
export interface IOrderDateTypeFilter {
  label: string;
  value: string;
  type: string;
}

/**
 * Order details options model
 */
export interface IOrderDetailsOptionsModel {
  users: any;
  userGroups: any;
}

/**
 * Order export
 */
export interface IOrderExportModel {
  icon: string;
  label: string;
  target: string;
  url: string;
}

/**
 * Order Filters Model
 */
export interface IOrderFiltersModel {
  dateStart?: Date;
  dateEnd?: Date;
  dateType?: any;
  orderType?: any;
  deliveryStatuses?: Array<{ label: string; selected: boolean }>;
  fulfillmentGroups?: Array<{
    code: string;
    index: number;
    label: string;
    selected: boolean;
  }>;
  jobFilter?: any;
  orderedForFilter?: Array<{ id: string; firstName: string; lastName: string }>;
  orderNumberFilter?: string;
  organizationFilter?: Array<{
    id: string;
    firstName: string;
    lastName: string;
  }>;
  organizations?: Array<{ id: string; firstName: string; lastName: string }>;
  productionStatuses?: Array<{
    code: string;
    label: string;
    selected: boolean;
  }>;
  lastId?: string;
  limit?: number;
  orderShippingTypes?: Array<{ label: string; selected: boolean }>;
  orderStatuses?: Array<{ label: string; selected: boolean }>;
  seatTypes?: Array<{ label: string; selected: boolean }>;
  userGroups?: Array<{ id: string; label: string; selected: boolean }>;
  users?: Array<{ id: string; firstName: string; lastName: string }>;
}

/**
 * Order item model
 */
export interface IOrderItemModel {
  id: string;
  label: string;
  attributes: Array<any>;
  dimensions: Array<any>;
  group: any;
  insulations: Array<any>;
  joints: Array<any>;
  materials: Array<any>;
  notes: string;
  quantity: number;
  referenceNumber: number;
  specifications: Array<any>;
  shipping: {
    status: any;
  };
  tag: string;
  type: string;
}

/**
 * Order message API model
 */
export interface IOrderMessageAPIModel {
  message: string;
  orderIndex: string;
  sendNotifications?: boolean;
}

/**
 * Order message model
 */
export interface IOrderMessageModel {
  dateCreated: Date;
  dateModified: Date;
  id: string;
  isEditing: boolean;
  isDeleting: boolean;
  type: string;
  user: IUserModel;
  message: string;
  dateTimeDisplay: string;
  visibility: number;
}

/**
 * Order message unparsed from API
 */
export interface IOrderMessageModelRaw {
  date_created: string;
  date_modified: string;
  id: string;
  user: IUserModel;
  user_message: string;
  transaction_message: string;
  visibility: number;
}

/**
 * Order tabs
 */
export interface IOrderTabs {
  canToggle?: boolean;
  columns?: Array<any>;
  key?: string;
  label?: string;
  iconKey?: string;
  isActive?: boolean;
  isVisible?: boolean;
  isCustom?: boolean;
}

/**
 * Order UI model
 */
export interface IOrderFormModel {
  assembly?: {
    id: string;
    link: string;
  };
  attachments?: Array<IOrderAttachmentModel>;
  calculations?: {
    fabrication: any;
    weight: any;
    price: any;
  };
  details?: {
    id: string;
    index: string;
    number: string;
    orderedBy: any;
    orderedFor: any;
    organization: string;
    measuredBy: string;
    tag: string;
    userGroup: any;
    poNumber: string;
  };
  dates?: {
    ordered: any;
    productionStart: any;
    productionEnd: any;
    requested: any;
    delivery: any;
  };
  delivery?: {
    address?: IOrderDeliveryAddressModel;
    option: {
      value: string;
    };
    loadingZone: {
      id: string;
      label: string;
    };
    startTime: {
      objectValue: any;
      readValue: any;
    };
    endTime: {
      objectValue: any;
      readValue: any;
    };
    vehicle: {
      assignments: Array<any>;
      assignmentOptions: Array<IVehicleAssignmentModel>;
    };
  };
  downloads?: Array<any>;
  exports?: Array<any>;
  formFields?: Array<IOrderFormFieldsModel>;
  fulfillment?: {
    groups: Array<{
      index: string;
      code: string;
      label: string;
      adjustmentsDataSource: {
        data: Array<any>;
      };
      fittingsDataSource: {
        data: Array<any>;
      };
      fittingsSelection: {
        hasValue: Function;
        isSelected: Function;
        selected: Array<any>;
        toggle: Function;
      };
      fittingsUpdating: boolean;
      fittingStatusesDropdownValue: any;
      exports: any;
      isUpdatingOrder: boolean;
      module: any;
      productsDataSource: { data: Array<any> };
      productsUpdating: boolean;
      productsSelection: {
        hasValue: Function;
        isSelected: Function;
        selected: Array<any>;
        toggle: Function;
      };
      productStatusesDropdownValue: any;
      status: any;
      trackerItemsDataSource: { data: Array<any> };
    }>;
  };
  instructions?: string;
  job?: IJobFormModel;
  messages?: {
    valuesDisplayed: Array<IOrderMessageModel>;
    valuesUnfiltered: Array<IOrderMessageModel>;
  };
  modelLink?: string;
  reports?: Array<any>;
  statuses?: {
    overall: any;
    production: any;
    shipping: any;
  };
  type?: string;
  isLookahead?: boolean;
  isModelViewerEnabled?: boolean;
  isEcommerce?: boolean;
}

export interface IOrderFormFieldsModel {
  label: string;
  uid: string;
  type: string;
  userGroups?: Array<{ uid: string }>;
  options?: Array<any>;
}

/**
 * Order view settings
 */
export interface IOrderViewSettingsModel {
  groups?: {
    fulfillment?: {
      showEmpty: boolean;
    };
  };
  page: number;
  pageSize: number;
  selected?: {
    columns: any;
    groups: {
      fulfillment?: {
        total?: number;
        indexes?: Array<any>;
      };
      user: {
        total?: number;
        ids?: Array<any>;
      };
    };
    statuses?: {
      order: {
        total: number;
        ids: Array<any>;
      };
      production: {
        total: number;
        indexes: Array<any>;
      };
      shipping: {
        total: number;
        indexes: Array<any>;
      };
    };
    types: {
      seat: {
        total: number;
        values: Array<any>;
      };
      shipping: {
        total: number;
        values: Array<any>;
      };
    };
  };
  selectedTab?: IOrderTabs;
  sortKey: string;
  sortDir: SortDirection;
}

/**
 * Order item tracker model
 */
export interface ITrackerItemModel {
  id: string;
  label: string;
  notes: string;
  quantity: number;
  tag: string;
}

/**
 * Loading Zone API Model
 */
export interface ILoadingZoneApiModel {
  id: string;
  label: string;
}

/**
 * Material Option
 */
export interface IMaterialOption {
  id: number;
  label: string;
}

/**
 * Order Attachment Model
 */
export interface IOrderAttachmentModel {
  index: number;
  filesize: any;
  name: string;
  originalFileName: string;
  url: string;
  description: string;
  isRemoving: false;
}

/**
 * Order delivery address model
 */
export interface IOrderDeliveryAddressModel {
  city: string;
  company: string;
  country: IBcxGeolocationModel;
  phone: string;
  state: {
    id: string;
    label: string;
  };
  street: string;
  zipcode: string;
  instructions: string;
}

/**
 * Order export
 */
export interface IOrderExportModel {
  icon: string;
  label: string;
  target: string;
  url: string;
}

/**
 * Order model
 */
export interface IOrderModel {
  id: string;
  adjustments: any;
  attachments: any;
  company: any;
  delivery: {
    date: any;
    address: any;
    assignments: any;
    option: any;
    start: any;
    status: any;
    time: any;
    vehicle: any;
  };
  downloads: any;
  exports: any;
  fulfillment: any;
  index: string;
  isUpdating?: boolean;
  job: {
    label: string;
    override: any;
  };
  meta: any;
  modelLink: any;
  number: string;
  ordered: any;
  orderedBy: any;
  orderedFor: any;
  orderDeliveryStatuses: any;
  orderFulfillmentGroups: any;
  orderFulfillmentStatuses: any;
  messages: number;
  production: any;
  reports: any;
  resources: any;
  result: any;
  requested: any;
  specialInstructions: string;
  status: any;
  settings: {
    canEditEcomJobOverride: boolean;
  };
  totals: {
    shopLabour: any;
    shopLabourComplete: any;
    shopLabourRemain: any;
    price: any;
    weight: any;
  };
  type: string;
  visibleSelectedFgs: any;
}

/**
 * Shipping type model
 */
export interface IShippingTypeModel {
  label: string;
  selected: boolean;
  sort: number;
  status: string;
  value: string;
}

/**
 * Standard BCX value with value, type, unit structure
 */
export interface IValueModel {
  value: number;
  type: string;
  unit: string;
}

/**
 * Table column options
 */
export interface ITableColumnOptions {
  canToggle?: boolean;
  description?: string;
  key?: string;
  label?: string;
  selected?: boolean;
  visibility: {
    orderTypes: Array<string>;
    tabs: Array<string>;
  };
}

/**
 * Tracker item model
 */
export interface ITrackerItemModel {
  id: string;
  label: string;
  notes: string;
  quantity: number;
  tag: string;
}

/**
 * User group model
 */
export interface IUserGroupModel {
  id: string;
  label: string;
}

export interface IShippingOptionApiModel {
  id: string;
  label: string;
  value: string;
}

export interface IOrderApiFilterParams {
  lastId: string | null;
  limit: number | null;
  orderType: string;
  orderShippingTypes: Array<any>;
  orderStatuses: Array<any>;
  productionStatuses: Array<any>;
  deliveryStatuses: Array<any>;
  fulfillmentGroups: Array<any>;
  seatTypes: Array<any>;
  userGroups: Array<any>;
}

/**
 * Order section.
 */
export interface IOrderSections {
  attachments: IOrderState;
  dates: IOrderState;
  details: IOrderState;
  formFields: IOrderState;
  instructions: IOrderState;
  job: IOrderState;
  massages: IOrderState;
  delivery: IOrderState;
  statuses: IOrderState;
}

export interface IOrderShippingNotifications {
  index: number;
  number: number;
}

/**
 * Order state.
 */
interface IOrderState {
  beforeChanges?: string;
  isChanges?: boolean;
  isEditing?: boolean;
  isLoading?: boolean;
  isSaving?: boolean;
}

/**
 * Raw order input.
 */
export interface IRawInputOrder {
  assembly: any;
  attachments: any;
  calculations: any;
  delivery: any;
  downloads: any;
  exports: any;
  formFields: any;
  fulfillment: any;
  id: any;
  index: any;
  job: any;
  lookahead: any;
  measuredBy: any;
  messages: any;
  modelLink: any;
  number: any;
  ordered: any;
  orderedBy: any;
  orderedFor: any;
  poNumber: any;
  production: any;
  reports: any;
  requested: any;
  specialInstructions: any;
  status: any;
  source: { vendor: any };
  tag: any;
  type: any;
  userGroup: any;
  isLookaheadOrder: boolean;
}
